import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Tab, Tabs} from "@mui/material";
import {OpenInNew} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useHistory} from "react-router-dom";
import {DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import {useDataGridSavedState} from "../../../../../../../packages/data-grid";
import useSearch from "../../../../../../hooks/use-search";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import PurchasesSearchSection from "../../../../../../components/search-sections/purchases";
import {ListPageShell, PageActions} from "../../../../../../components/page-shell";
import {routeFunctions, routes} from "../../../../../../../core/models/static/routes/app-routes";
import Utils, {formatMoney} from "../../../../../../../core/services/utils/utils";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import {useAppSelector} from "../../../../../../redux";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {PurchaseHistoryStatuses} from "../../constants";
import PurchaseHistoryListHistorySection from "./sections/history";
import PurchaseHistoryListItemsSection from "./sections/items";
import PurchaseHistoryListReportSection from "./sections/report";

const tabs = {history: 'history', items: 'items', reports: 'reports'};

const historySaveConfig = {name: 'PurchaseHistoryListHistoryDataGrid', version: 2};
const itemsSaveConfig = {name: 'PurchaseHistoryListItemsDataGrid', version: 1};
const reportSaveConfig = {name: 'PurchaseHistoryListReportDataGrid', version: 1};

const historyStatusCycle = [
    PurchaseHistoryStatuses.received,
    PurchaseHistoryStatuses.invoiced,
    PurchaseHistoryStatuses.partial,
    PurchaseHistoryStatuses.closed,
];

/**
 * Compares a row date string (YYYY-MM-DD…) against optional shell start/end filters.
 */
const rowDateInRange = (dateStr, startDate, endDate) => {
    if (!startDate && !endDate) {
        return true;
    }
    const day = dateStr?.toString()?.slice(0, 10);
    if (!day) {
        return true;
    }
    const start = startDate?.toString()?.slice(0, 10);
    const end = endDate?.toString()?.slice(0, 10);
    if (start && day < start) {
        return false;
    }
    if (end && day > end) {
        return false;
    }
    return true;
};

const mockHistory = (scopeKey) => Array.from({length: 10}, (_, i) => ({
    id: i + 1,
    historyNo: `PH-${scopeKey}-${2000 + i}`,
    date: `2025-${String((i % 12) + 1).padStart(2, '0')}-10`,
    status: historyStatusCycle[i % historyStatusCycle.length],
    total: 900 + i * 40,
    currency: 'CAD',
    vendor: {id: Number(scopeKey) || i + 1, name: `Vendor ${i + 1}`, title: `Vendor ${i + 1}`},
    vendorId: Number(scopeKey) || i + 1,
}));

const mockItems = (scopeKey) => Array.from({length: 14}, (_, i) => ({
    id: i + 1,
    vendorPartNo: `VP-H-${i}`,
    partNo: `P-H-${i}`,
    description: `History line ${i + 1}`,
    qty: (i % 4) + 1,
    unitPrice: 30 + i,
    total: ((i % 4) + 1) * (30 + i),
    serials: i % 4 === 0 ? `SN-H-${scopeKey}-${i}` : '',
    date: `2025-${String((i % 12) + 1).padStart(2, '0')}-10`,
    status: historyStatusCycle[i % historyStatusCycle.length],
    vendor: {id: Number(scopeKey) || i + 1, name: `Vendor ${i + 1}`, title: `Vendor ${i + 1}`},
    vendorId: Number(scopeKey) || i + 1,
}));

/**
 * Report-tab rows: invoice-style money + balance fields (shell data until APIs exist).
 */
const mockReport = (scopeKey) => Array.from({length: 10}, (_, i) => {
    const total = 900 + i * 40;
    const discountAmount = i % 3 === 0 ? 25 : 0;
    const avgCost = 40 + i * 2;
    const subTotal = total + discountAmount;
    const balance = i % 2 === 0 ? 0 : total * 0.2;
    return {
        id: i + 1,
        historyNo: `PH-${scopeKey}-${2000 + i}`,
        date: `2025-${String((i % 12) + 1).padStart(2, '0')}-10`,
        vendorPO: `VPO-${100 + i}`,
        avgCost,
        discountAmount,
        subTotal,
        total,
        balance,
        isOutstanding: balance > 0,
        currency: 'CAD',
        status: historyStatusCycle[i % historyStatusCycle.length],
        vendor: {id: Number(scopeKey) || i + 1, name: `Vendor ${i + 1}`, title: `Vendor ${i + 1}`},
        vendorId: Number(scopeKey) || i + 1,
    };
});

/**
 * Dual-scope purchase history list (system + vendor).
 * // TODO: replace mocks with CleanAF purchase-history APIs.
 */
const PurchaseHistoryListView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.purchases.historyList'});
    const history = useHistory();
    const defaultVendor = useAppSelector((state) => state.vendorInformation);
    const operatorAccessLevels = useAccessLevels();
    const [tab, setTab] = useState(tabs.history);
    const [data, setData] = useState([]);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('purchase-history-list');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const [, historySortBy, historyPageSize] = useDataGridSavedState({
        columns: [],
        ...historySaveConfig,
    });
    const [, itemsSortBy, itemsPageSize] = useDataGridSavedState({
        columns: [],
        ...itemsSaveConfig,
    });
    const [, reportSortBy, reportPageSize] = useDataGridSavedState({
        columns: [],
        ...reportSaveConfig,
    });

    /**
     * Notebook tags: vendor-scoped history or system purchase list tags.
     */
    useEffect(() => {
        if (defaultVendor?.code) {
            configureNotebookTags((tags) => tags.vendorPurchaseHistory(defaultVendor.code));
            configureBehavioralTool((tags) => tags.vendorPurchaseHistory(defaultVendor.code));
        } else {
            configureNotebookTags((tags) => tags.purchaseList);
            configureBehavioralTool((tags) => tags.purchaseList);
        }
    }, [configureBehavioralTool, configureNotebookTags, defaultVendor?.code]);

    const prepareSearchData = useCallback((args) => ({
        keyword: args.filters?.keyword?.toString() ?? '',
        partNo: args.filters?.partNo?.toString() ?? '',
        paginationInfo: args.paginationInfo,
        tab: args.tab ?? tab,
        vendorId: defaultVendor?.id,
        vendors: defaultVendor?.id ? [] : (args.vendors ?? []),
        status: args.filters?.status,
        startDate: args.filters?.startDate,
        endDate: args.filters?.endDate,
    }), [defaultVendor?.id, tab]);

    /**
     * Shell search over mock data. // TODO: live APIs
     */
    const search = useCallback(async (args) => {
        const request = prepareSearchData(args);
        await Utils.wait(200);
        if (!isMounted()) return;
        const scopeKey = request.vendorId ?? 'sys';
        let source;
        switch (request.tab) {
            case tabs.items:
                source = mockItems(scopeKey);
                break;
            case tabs.reports:
                source = mockReport(scopeKey);
                break;
            case tabs.history:
            default:
                source = mockHistory(scopeKey);
                break;
        }
        const keyword = request.keyword.trim().toLowerCase();
        const partNo = request.partNo.trim().toLowerCase();
        let filtered = source;
        if (keyword) {
            filtered = filtered.filter((row) => Object.values(row).some((v) =>
                typeof v !== 'object' && String(v).toLowerCase().includes(keyword)));
        }
        if (partNo && request.tab === tabs.items) {
            filtered = filtered.filter((row) =>
                String(row.partNo ?? '').toLowerCase().includes(partNo)
                || String(row.vendorPartNo ?? '').toLowerCase().includes(partNo));
        }
        if (!request.vendorId && request.vendors?.length) {
            const ids = new Set(request.vendors.map((v) => v.id));
            filtered = filtered.filter((row) => ids.has(row.vendor?.id ?? row.vendorId));
        }
        if (request.status) {
            filtered = filtered.filter((row) =>
                String(row.status ?? '').toLowerCase() === String(request.status).toLowerCase());
        }
        filtered = filtered.filter((row) => rowDateInRange(row.date, request.startDate, request.endDate));
        const start = (request.paginationInfo.currentPage - 1) * request.paginationInfo.pageSize;
        setData(filtered.slice(start, start + request.paginationInfo.pageSize));
        args.setPaginationInfo((prev) => ({...prev, length: filtered.length}));
    }, [isMounted, prepareSearchData]);

    const activePageSize = useMemo(() => {
        if (tab === tabs.items) return itemsPageSize;
        if (tab === tabs.reports) return reportPageSize;
        return historyPageSize;
    }, [historyPageSize, itemsPageSize, reportPageSize, tab]);

    const activeSortBy = useMemo(() => {
        if (tab === tabs.items) return itemsSortBy;
        if (tab === tabs.reports) return reportSortBy;
        return historySortBy;
    }, [historySortBy, itemsSortBy, reportSortBy, tab]);

    const initialPaginationInfo = useMemo(() => ({
        ...DefaultPaginationInfo,
        pageSize: activePageSize ?? DefaultPaginationInfo.pageSize,
    }), [activePageSize]);

    const searchData = useSearch(search, {
        paginationInfo: initialPaginationInfo,
        orderBy: activeSortBy,
        tab,
        vendors: [],
    });

    useEffect(() => {
        searchData.setTab(tab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    /**
     * Navigates to vendor-scoped history single (required for detail).
     */
    const navigateToHistory = useCallback((row, vendorId) => {
        const resolved = vendorId ?? row.vendor?.id ?? row.vendorId;
        if (!resolved) return;
        history.push(routeFunctions.vendor.purchases.history.single(resolved, row.id));
    }, [history]);

    /**
     * Formats report rows for CSV export (shell columns).
     */
    const exportReportToCsv = useCallback(async () => {
        return {
            title: t('exportTitle'),
            body: data.map((row) => ({
                [t('reportColumns.historyNo')]: row.historyNo ?? '',
                [t('reportColumns.vendor')]: row.vendor?.name ?? row.vendor?.title ?? '',
                [t('reportColumns.date')]: row.date ?? '',
                [t('reportColumns.vendorPO')]: row.vendorPO ?? '',
                [t('reportColumns.avgCost')]: formatMoney(row.avgCost ?? 0),
                [t('reportColumns.discount')]: formatMoney(row.discountAmount ?? 0),
                [t('reportColumns.subtotal')]: formatMoney(row.subTotal ?? 0),
                [t('reportColumns.total')]: formatMoney(row.total ?? 0),
                [t('reportColumns.balance')]: formatMoney(row.balance ?? 0),
            })),
        };
    }, [data, t]);

    /**
     * Tab change resets rows and grid pagination preferences.
     */
    const onTabChanged = useCallback((_e, newTab) => {
        setData([]);
        setTab(newTab);
        searchData.setTab(newTab);
        if (newTab === tabs.items) {
            searchData.setPaginationInfo((prev) => ({
                ...prev,
                pageSize: itemsPageSize ?? DefaultPaginationInfo.pageSize,
            }));
            searchData.setOrderBy(itemsSortBy);
        } else if (newTab === tabs.reports) {
            searchData.setPaginationInfo((prev) => ({
                ...prev,
                pageSize: reportPageSize ?? DefaultPaginationInfo.pageSize,
            }));
            searchData.setOrderBy(reportSortBy);
        } else {
            searchData.setPaginationInfo((prev) => ({
                ...prev,
                pageSize: historyPageSize ?? DefaultPaginationInfo.pageSize,
            }));
            searchData.setOrderBy(historySortBy);
        }
    }, [historyPageSize, historySortBy, itemsPageSize, itemsSortBy, reportPageSize, reportSortBy, searchData]);

    const title = defaultVendor?.name
        ? t('titleVendor', {name: defaultVendor.name})
        : t('titleSystem');

    const actionItems = useMemo(() => ([
        {
            id: 'view-all',
            label: t('viewAll'),
            icon: <OpenInNew className={'icon small'}/>,
            hidden: !defaultVendor?.id,
            onClick: () => history.push(routes.purchase.history),
        },
    ]), [defaultVendor?.id, history, t]);

    const filters = useMemo(() => (
        <PurchasesSearchSection
            searchData={searchData}
            hideVendorsMultiSelect={!!defaultVendor?.id}
            showPartNumberField={tab === tabs.items}
            statusVariant={'history'}
        />
    ), [defaultVendor?.id, searchData, tab]);

    return (
        <ListPageShell
            title={title}
            subtitle={t('shellNotice')}
            actions={<PageActions items={actionItems}/>}
            filters={filters}
            tabs={(
                <Tabs value={tab} onChange={onTabChanged}>
                    <Tab value={tabs.history} label={t('tabs.history')}/>
                    <Tab value={tabs.items} label={t('tabs.items')}/>
                    <Tab value={tabs.reports} label={t('tabs.reports')}/>
                </Tabs>
            )}
        >
            {
                tab === tabs.history &&
                <PurchaseHistoryListHistorySection
                    data={data}
                    searchData={searchData}
                    dataGridSaveConfig={historySaveConfig}
                    navigateToHistory={navigateToHistory}
                    defaultVendor={defaultVendor}
                />
            }
            {
                tab === tabs.items &&
                <PurchaseHistoryListItemsSection
                    data={data}
                    searchData={searchData}
                    dataGridSaveConfig={itemsSaveConfig}
                />
            }
            {
                tab === tabs.reports &&
                <PurchaseHistoryListReportSection
                    data={data}
                    searchData={searchData}
                    dataGridSaveConfig={reportSaveConfig}
                    navigateToHistory={navigateToHistory}
                    defaultVendor={defaultVendor}
                    getExportInfo={operatorAccessLevels.packages.purchasing.exportPurchaseHistory ? exportReportToCsv : undefined}
                />
            }
        </ListPageShell>
    );
};

export default PurchaseHistoryListView;
